import {Component, EventEmitter, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ObjectUtils} from '../../../../../../../shared/utils/object.utils';
import {SaleInvoiceModel} from '../../entity/saleInvoice.model';
import {BaseView} from '../../../../../../core/view/base.view';
import {DocumentCreateFormComponent} from '../../../../../../document/common/api/component/document-create-form/document-create-form.component';
import {SALE_DIRECTION, SaleModel} from '../../../../../api/entity/sale.model';
import {SaleSummaryModel} from '../../../../../api/entity/saleSummary.model';
import {SaleInvoiceConfigurationFindOneByAccountBusinessCodeService} from '../../../modules/configuration/impl/service/saleInvoiceConfigurationFindOneByAccountBusinessCode.service';
import {AuthService} from '../../../../../../auth/api/service/auth.service';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {SaleInvoiceConfigurationModel} from '../../../modules/configuration/api/entity/saleInvoiceConfiguration.model';
import {MomentAdapter} from '../../../../../../../shared/utils/moment.utils';
import {SaleInvoiceStatus} from '../../entity/saleInvoiceSummary.model';
import {SaleInvoiceSaveService} from '../../service/save/saleInvoiceSave.service';
import {SaleInvoiceSavePayload} from '../../service/save/saleInvoiceSave.payload';
import {SaleFindOneByIdService} from '../../../../../impl/service/SaleFindOneById.service';
import {
    CreateUpdateDefaultComponent
} from "../../../../../../core/component/create-update-default/create-update-default.component";
import {
    SelectElement,
    SelectElementWithBusinessValue
} from "../../../../../../../components/common/select/select.model";
import {
    SaleInvoiceConfigurationBookModel
} from "../../../modules/configuration/api/entity/saleInvoiceConfigurationBook.model";

@Component({
    selector: 'app-sale-invoice-save',
    templateUrl: './sale-invoice-save.component.html',
    providers: [
        SaleInvoiceSaveService,
        SaleFindOneByIdService,
        SaleInvoiceConfigurationFindOneByAccountBusinessCodeService
    ]
})
export class SaleInvoiceSaveComponent extends BaseView implements OnDestroy {

    SaleDirectionIn = SALE_DIRECTION.SALE_DIRECTION_IN;
    documentFormGroup: UntypedFormGroup;
    formGroup: UntypedFormGroup;
    reportFormGroup: UntypedFormGroup;
    sale: SaleSummaryModel;
    accountBusinessCode: string;
    configuration: SaleInvoiceConfigurationModel;

    subscriptions: Subscription[] = [];
    entity: SaleInvoiceModel;
    bookElements: SelectElementWithBusinessValue<SaleInvoiceConfigurationBookModel>[] = [];

    @Input() id: number;
    @Input() saleId: number;
    @Input() disableManualDocument = false;
    @Input() hideSaveButton = false;

    @Output() entitySavedEvent = new EventEmitter<SaleInvoiceModel>();

    @ViewChild('documentCreateForm') documentCreateForm: DocumentCreateFormComponent;
    @ViewChild('createUpdateDefaultComponent') createUpdateDefaultComponent : CreateUpdateDefaultComponent<any, any, any>;

    constructor(public service: SaleInvoiceSaveService,
                public saleService: SaleFindOneByIdService,
                public authService: AuthService,
                public saleInvoiceConfigurationService: SaleInvoiceConfigurationFindOneByAccountBusinessCodeService,
                private formBuilder: UntypedFormBuilder) {
        super();

        this.reportFormGroup = new UntypedFormGroup({});
        this.documentFormGroup = new UntypedFormGroup({});
        this.formGroup = this.formBuilder.group({
            summary: new UntypedFormGroup({
                id: new UntypedFormControl(undefined)
            }),
            date: new UntypedFormControl(MomentAdapter.current().get()),
            statusValue: new UntypedFormControl(SaleInvoiceStatus.SALE_INVOICE_STATUS_CREATED, Validators.required),
            saleId: new UntypedFormControl(undefined, Validators.required),
            number: new UntypedFormControl(undefined),
            documentLabel: new UntypedFormControl('Facture', Validators.required),
            bookId: new UntypedFormControl(undefined, Validators.required)
        });
        this.formGroup.controls.bookId.valueChanges.subscribe((value) => this.refreshNumber());
        this.refreshDocumentLabel();
        this.subscriptions.push(this.authService.account.subscribe(value => this.accountBusinessCode = value?.summary.businessCode));

    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(value => value?.unsubscribe());
    }

    refreshDocumentLabel() {
        let documentLabel = 'Facture';
        if (this.formGroup.value.number) {
            documentLabel += ' n° ' + this.formGroup.value.number;
        }
        this.formGroup.patchValue({
            documentLabel
        })
    }

    get documentFormGroupAdded() {
        return !!this.formGroup.controls.document;
    }

    get saleLabel() {
        if (!this.sale) {
            return 'Achat / Vente';
        }
        return this.sale.direction.value === this.SaleDirectionIn ? 'Achat' : 'Vente';
    }

    addDocument() {
        this.formGroup.addControl('document', this.documentFormGroup);
        this.formGroup.removeControl('report');
    }

    removeDocument() {
        this.formGroup.removeControl('document');
        if (this.sale.direction.value !== this.SaleDirectionIn) {
            this.formGroup.addControl('report', this.reportFormGroup);
        }

    }

    entityLoaded(entity: SaleInvoiceModel) {
        console.log('entityLoaded', entity);
        this.entity = entity;
        const summary = entity.summary;
        this.formGroup.patchValue({
            ...summary,
            summary: {
                id: summary.id
            },
            statusValue: entity.summary.status?.value,
            documentLabel: entity.summary.document?.label ? entity.summary.document.label : '',
        });

        this.sale = entity.sale;
        this.onSaleSummaryChanged();
        this.refreshDocumentLabel();
    }

    refreshSelectedBook() {
        console.log('refreshSelectedBook', this.sale, this.bookElements);
        if (this.sale && this.bookElements?.length > 0) {
            let selectedElement: SelectElementWithBusinessValue<any>;
            if (this.sale.responsibleUser) {
                selectedElement = this.bookElements
                    .filter(value => value.businessValue.user)
                    .find(value => value.businessValue.user.id == this.sale.responsibleUser.id);
            } else {
                selectedElement = this.bookElements
                    .filter(value => value.businessValue.user)
                    .find(value => value.businessValue.user.id == this.authService.userSnapshot?.summary?.id);
            }

            if (!selectedElement) {
                selectedElement = this.bookElements[0];
            }
            this.formGroup.patchValue({
                bookId: selectedElement.value
            });
        }
    }

    saleInvoiceConfigurationLoaded(entity: SaleInvoiceConfigurationModel) {
        this.configuration = entity;
        this.refreshNumber();
        this.bookElements = this.configuration.books
            .filter(value => value.active === true)
            .map(value => {
                return new SelectElementWithBusinessValue(value.id, value.label, value);
            });
        this.refreshSelectedBook();
    }

    saleLoaded(entity: SaleModel) {
        this.sale = entity?.summary;
        this.onSaleSummaryChanged();
    }

    onSaleSummaryChanged() {
        this.formGroup.patchValue({
            saleId: this.sale.id
        });
        this.refreshNumber();
        this.removeDocument();
        this.refreshSelectedBook();
    }

    refreshNumber() {
        if (this.sale && this.sale.direction.value !== this.SaleDirectionIn) {
            const selectedBookId = this.formGroup.controls.bookId.value;
            if (selectedBookId) {
                const selectedBook = this.configuration.books.find(book => book.id === selectedBookId);
                if (selectedBook) {
                    if (selectedBook.autoGenerateNumber === true) {
                        this.formGroup.patchValue({
                            number: selectedBook.prefix + selectedBook.nextNumber,
                        });
                        this.formGroup.controls.number.disable();
                        return;
                    }
                }
            }
            this.formGroup.controls.number.enable();
        }
    }

    buildPayload = (): SaleInvoiceSavePayload => {
        console.log(this.formGroup.value);
        const payload = ObjectUtils.assign(new SaleInvoiceSavePayload(), this.formGroup.value, []);
        console.log(payload);
        if (this.documentCreateForm) {
            payload.document = this.documentCreateForm.buildPayload();
        }

        if (this.sale.direction.value === SALE_DIRECTION.SALE_DIRECTION_OUT) {
            payload.number = undefined;
        }
        return payload;
    }

    entitySaved(entity: SaleInvoiceModel) {
        this.entityLoaded(entity);
        this.entitySavedEvent.emit(entity);
    }

    save() {
        this.createUpdateDefaultComponent.save();
    }

    get saveLoading(): Observable<boolean> {
        return this.createUpdateDefaultComponent?.service ? this.createUpdateDefaultComponent.service.loading$
            : new BehaviorSubject<boolean>(false);
    }
}
